// apps/web/src/components/DocCard.tsx
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FileText, Trash2 } from 'lucide-react';
import { Card, Badge, IconButton } from '@hump/ui';
import type { LibraryDocument } from '@hump/shared-types';
import { useLibraryStore } from '../stores/library';
import { useConfirmStore } from './ConfirmDialog';

const sourceTypeLabel: Record<string, string> = {
  md: 'MD',
  pdf: 'PDF',
  epub: 'EPUB',
  docx: 'DOCX',
  url: 'URL',
};

export function DocCard({ doc }: { doc: LibraryDocument }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const deleteDoc = useLibraryStore((s) => s.deleteDoc);

  const confirmDialog = useConfirmStore((s) => s.confirm);

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    const ok = await confirmDialog({
      title: '删除文档',
      message: `确定删除「${doc.title}」？关联知识点会保留。`,
      confirmText: '删除',
      danger: true,
    });
    if (ok) deleteDoc(doc.id);
  }

  return (
    <Card onClick={() => navigate(`/reader/${doc.id}`)}>
      <div className="flex items-start gap-3">
        <FileText className="w-6 h-6 text-camel shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-charcoal dark:text-charcoal-light truncate">
            {doc.title}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="default">{sourceTypeLabel[doc.sourceType] ?? doc.sourceType}</Badge>
            {doc.progress > 0 && (
              <span className="text-xs text-charcoal/50 dark:text-charcoal-light/50">
                {Math.round(doc.progress * 100)}%
              </span>
            )}
          </div>
        </div>
        <IconButton size="sm" onClick={handleDelete} aria-label={t('common.delete')}>
          <Trash2 className="w-4 h-4" />
        </IconButton>
      </div>
    </Card>
  );
}
